import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Language, languages, languageNames } from '@/utils/i18n';

function FlagFI() {
  return (
    <svg viewBox="0 0 64 48" className="flag" width="24" height="18" aria-hidden="true">
      <rect width="64" height="48" fill="#fff" />
      <rect x="0" y="18" width="64" height="12" fill="#003580" />
      <rect x="22" y="0" width="12" height="48" fill="#003580" />
    </svg>
  );
}

function FlagEN() {
  return (
    <svg viewBox="0 0 64 48" className="flag" width="24" height="18" aria-hidden="true">
      <clipPath id="en-flag-clip">
        <rect x="0" y="0" width="64" height="48" rx="2" />
      </clipPath>
      <g clipPath="url(#en-flag-clip)">
        <path fill="#012169" d="M0 0h64v48H0z" />
        <path stroke="#fff" strokeWidth="8" d="M-8 0l80 60M72 0L-8 60" />
        <path stroke="#C8102E" strokeWidth="4" d="M-8 0l80 60M72 0L-8 60" />
        <path fill="#fff" d="M26 0h12v48H26zM0 18h64v12H0z" />
        <path fill="#C8102E" d="M28 0h8v48h-8zM0 20h64v8H0z" />
      </g>
    </svg>
  );
}

function FlagSV() {
  return (
    <svg viewBox="0 0 64 48" className="flag" width="24" height="18" aria-hidden="true">
      <rect width="64" height="48" fill="#006AA7" />
      <rect x="0" y="20" width="64" height="8" fill="#FECC00" />
      <rect x="18" y="0" width="8" height="48" fill="#FECC00" />
    </svg>
  );
}

function FlagTH() {
  return (
    <svg viewBox="0 0 64 48" className="flag" width="24" height="18" aria-hidden="true">
      <rect width="64" height="48" fill="#fff" />
      <rect y="0" width="64" height="8" fill="#A51931" />
      <rect y="8" width="64" height="8" fill="#fff" />
      <rect y="16" width="64" height="16" fill="#2D2A4A" />
      <rect y="32" width="64" height="8" fill="#fff" />
      <rect y="40" width="64" height="8" fill="#A51931" />
    </svg>
  );
}

const flagComponents: Record<Language, React.FC> = {
  fi: FlagFI,
  en: FlagEN,
  sv: FlagSV,
  th: FlagTH,
};

export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const CurrentFlag = flagComponents[language];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const otherLanguages = languages.filter((l) => l !== language);

  return (
    <div className="lang-switcher" ref={containerRef}>
      <button
        type="button"
        className={`lang-toggle lang-${language}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Change language (current: ${languageNames[language]})`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <CurrentFlag />
      </button>

      {isOpen && (
        <nav className="lang-menu" role="menu" aria-label="Language menu">
          {otherLanguages.map((lang) => {
            const Flag = flagComponents[lang];
            return (
              <button
                key={lang}
                type="button"
                className="lang-option"
                role="menuitem"
                aria-label={languageNames[lang]}
                onClick={() => {
                  setLanguage(lang);
                  setIsOpen(false);
                }}
              >
                <Flag />
                <span className="code">{lang.toUpperCase()}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
